import { ErrorHandler, Options, request, SuccessHandler } from 'api/apiTS';
import { baseApi } from 'components/constants/Constants';
import { ProductDocument } from 'components/pages/products/ProductAsset/types';
import { Message } from 'context/Notifications';
import { Contact } from 'types/ContactCenter/Contact';
import { ContentPage } from 'types/global';
import { useAxiosWithAuth } from './useAxios';

export const createMessage = (
  contact: Contact,
  message: string,
  success: SuccessHandler<Message>,
  handleError: ErrorHandler
) => {
  const options = {
    method: 'POST',
    url: `${baseApi}/contacts/${contact.key}/messages`,
    headers: { 'Content-Type': 'application/json' },
    data: {
      message: message
    }
  };
  request(options, success, handleError);
};

export const getMessages = (
  contact: Contact,
  success: SuccessHandler<{
    page: ContentPage;
    content: Message[];
  }>,
  handleError?: ErrorHandler
) => {
  const filter = '?sort=created_at,desc&size=50';
  const options = {
    method: 'GET',
    url: `${baseApi}/contacts/${contact.key}/messages${filter}`,
    headers: { 'Content-Type': 'application/json' }
  };
  request(options, success, handleError);
};

export const close = (
  key: string,
  success: SuccessHandler,
  handleError: ErrorHandler
) => {
  const options = {
    method: 'POST',
    url: `${baseApi}/contacts/${key}/close`,
    headers: { 'Content-Type': 'application/json' }
  };
  request(options, success, handleError);
};
export const cancel = (
  key: string,
  success: SuccessHandler,
  handleError: ErrorHandler
) => {
  const options = {
    method: 'POST',
    url: `${baseApi}/contacts/${key}/cancel`,
    headers: { 'Content-Type': 'application/json' }
  };
  request(options, success, handleError);
};

export const uploadExistingProductAssets = (
  assets: string[],
  product: {
    key: string;
    segment: string;
    company: string;
  },
  contact: string,
  success: SuccessHandler,
  handleError: ErrorHandler
) => {
  const data = {
    product: {
      company: product.company,
      product: product.key,
      segment: product.segment
    },
    assets: assets
  };
  const options = {
    method: 'POST',
    url: `${baseApi}/contacts/${contact}/assets`,
    headers: { 'Content-Type': 'application/json' },
    data: data
  };
  request(options, success, handleError);
};

export const downloadContactAsset = (
  contact: Contact,
  asset: ProductDocument,
  success: SuccessHandler,
  handleError?: ErrorHandler
) => {
  const options: Options = {
    method: 'GET',
    url: `${baseApi}/contacts/${contact.key}/assets/${asset.key}/download`,
    headers: {},
    responseType: 'blob'
  };
  request(options, success, handleError);
};

export const retrieveProductAssets = (
  company: string,
  segment: string,
  product: string,
  success: SuccessHandler<{ content: ProductDocument[] }>,
  handleError?: ErrorHandler
) => {
  const options = {
    method: 'GET',
    url: `${baseApi}/companies/${company}/segments/${segment}/products/${product}/assets`,
    headers: { 'Content-Type': 'application/json' }
  };
  request(options, success, handleError);
};

export const loadAllAssets = (
  contact: Contact,
  success: SuccessHandler<{ content: ProductDocument[] }>,
  handleError?: ErrorHandler
) => {
  const options = {
    method: 'GET',
    url: `${baseApi}/contacts/${contact.key}/assets`,
    headers: { 'Content-Type': 'application/json' }
  };
  request(options, success, handleError);
};

export const useGetContactRequests = () =>
  useAxiosWithAuth<{
    content: Contact[];
    page: ContentPage;
  }>(
    {
      url: `${baseApi}/contacts`,
      method: 'GET'
    },
    { manual: true }
  );

export const useGetContactRequest = (key: string) =>
  useAxiosWithAuth<Contact>({
    url: `${baseApi}/contacts/${key}`,
    method: 'GET'
  });

export const fetchLastRequest = (
  requestType: string,
  handleSuccess: SuccessHandler<Contact>,
  handleError: ErrorHandler
) => {
  const options = {
    method: 'GET',
    url: `${baseApi}/contacts/last?type=${requestType}`,
    headers: { 'Content-Type': 'application/json' }
  };

  request(options, handleSuccess, handleError, undefined, undefined);
};

export const uploadContactAsset = (
  contact: Contact,
  asset: ProductDocument,
  file: File,
  success: SuccessHandler<ProductDocument>,
  handleError: ErrorHandler
) => {
  const data = new FormData();
  data.append(
    'asset',
    new Blob([JSON.stringify(asset)], { type: 'application/json' })
  );
  data.append('content', file);
  const options = {
    method: 'POST',
    url: `${baseApi}/contacts/${contact.key}/assets`,
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    data: data
  };

  request(options, success, handleError);
};
